import { apiClient } from './apiClient';
import {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
} from '@coffeeplexity/shared';

export const authApi = {
  // Login user
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/login', credentials);

    // Transform backend response to match AuthResponse format
    const authResponse: AuthResponse = {
      user: response.data.user,
      tokens: {
        access: response.data.access_token,
        refresh: response.data.refresh_token,
      },
    };

    return authResponse;
  },

  // Register new user
  register: async (userData: RegisterData): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/register', userData);

    // Transform backend response to match AuthResponse format
    const authResponse: AuthResponse = {
      user: response.data.user,
      tokens: {
        access: response.data.access_token,
        refresh: response.data.refresh_token,
      },
    };

    return authResponse;
  },

  // Logout user
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  // Get current user
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get('/auth/current-user');
    return response.data;
  },

  // Refresh token
  refreshToken: async (): Promise<{ access_token: string }> => {
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password
  resetPassword: async (
    token: string,
    newPassword: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  // Verify email
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/verify-email', { token });
    return response.data;
  },

  // Resend verification email
  resendVerification: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/resend-verification', {
      email,
    });
    return response.data;
  },

  // Update profile
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    const response = await apiClient.put('/auth/profile', userData);
    return response.data;
  },

  // Change password
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // Delete account
  deleteAccount: async (password: string): Promise<{ message: string }> => {
    const response = await apiClient.delete('/auth/account', {
      data: { password },
    });
    return response.data;
  },
};
